import type { ImageSet } from "@pax/api-contract/common";
import { Avatar, AvatarFallback, AvatarImage, cn } from "@pax/ui";
import { srcSet } from "@/lib/images";

/** Each width, in CSS pixels, an avatar is drawn at, and the class that draws it. */
const AVATAR_SIZES = {
  20: "size-5",
  24: "size-6",
  28: "size-7",
  32: "size-8",
  36: "size-9",
  48: "size-12",
  88: "size-22",
  104: "size-26",
} as const;

function initials(name: string, fallback: string) {
  return (name.trim() || fallback)
    .split(/\s+/)
    .map(word => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

/**
 * Somebody's picture, or the first letters of their name where there is none. `size` is the width
 * it's drawn at, which also picks the file the browser loads.
 */
export function AccountAvatar({
  name,
  image,
  size,
  fallback = name,
  className,
  fallbackClassName,
}: {
  name: string;
  image?: ImageSet | null;
  size: keyof typeof AVATAR_SIZES;
  fallback?: string;
  className?: string;
  fallbackClassName?: string;
}) {
  return (
    <Avatar className={cn(AVATAR_SIZES[size], className)}>
      {image ? (
        <AvatarImage src={image.sources[0].url} srcSet={srcSet(image)} sizes={`${size}px`} alt="" />
      ) : null}
      <AvatarFallback className={cn("text-xs font-medium", fallbackClassName)}>
        {initials(name, fallback)}
      </AvatarFallback>
    </Avatar>
  );
}
